import Modal from '@risksmart-app/components/src/modal';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ownerContributorAndViewerIds } from 'src/components/form';
import { ModalBodyWrapper } from 'src/components/form/form/ModalBodyWrapper';
import { useInsertInternalAuditReport } from 'src/hooks/mutations/internal-audit-report';
import InternalAuditReportForm from 'src/pages/internal-audit/reports/forms/internal-audit-report-form/InternalAuditReportForm';
import type { InternalAuditReportFormDataFields } from 'src/pages/internal-audit/reports/forms/internal-audit-report-form/internalAuditReportSchema';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

type InsertInternalAuditReportInput = Parameters<
  ReturnType<typeof useInsertInternalAuditReport>['insertInternalAuditReport']
>[0];

interface Props {
  parent: ObjectWithContributors;
  onDismiss: (saved: boolean) => void;
  onSave?: () => void;
}

const InternalAuditReportModal: FC<Props> = ({
  onDismiss,
  parent,
  onSave: parentOnSave,
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'internalAuditReports',
  });

  const { hasPermission: userCanCreate, loading: userCanCreateLoading } =
    useHasPermissionQuery('insert:internal_audit_report', parent);

  const { insertInternalAuditReport } = useInsertInternalAuditReport();

  const onSave = async (variables: InternalAuditReportFormDataFields) => {
    const object: InsertInternalAuditReportInput = {
      CustomAttributeData: variables.CustomAttributeData || undefined,
      ...ownerContributorAndViewerIds(variables),
      TagTypeIds: variables.tags?.map((t) => t.TagTypeId) || [],
      DepartmentTypeIds:
        variables.departments?.map((d) => d.DepartmentTypeId) || [],
      OriginatingItemId: parent.Id,
      CompletedByUser: variables.CompletedByUser?.value ?? null,
      Status: variables.Status,
      Title: variables.Title,
      Summary: variables.Summary,
      ActualCompletionDate: variables.ActualCompletionDate,
      NextTestDate: variables.NextTestDate,
      StartDate: variables.StartDate,
      TargetCompletionDate: variables.TargetCompletionDate,
      Outcome: variables.Outcome,
    };
    await insertInternalAuditReport(object);
    parentOnSave?.();
  };

  return (
    <Modal
      header={t('create_title')}
      visible={true}
      onDismiss={(event) => {
        // don't close modal on overlay click
        if (event.detail.reason === 'overlay') {
          return;
        }
        onDismiss(false);
      }}
      disableContentPaddings={true}
    >
      <InternalAuditReportForm
        readOnly={!userCanCreate || userCanCreateLoading}
        onSave={onSave}
        renderTemplate={(renderProps) => <ModalBodyWrapper {...renderProps} />}
        onDismiss={onDismiss}
      />
    </Modal>
  );
};

export default InternalAuditReportModal;
